import React from "react";
import {
  Select,
  MenuItem,
  Typography,
  Box,
  FormControl,
  TextField,
  Container,
  Stack,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { startOfDay, isBefore, isAfter, endOfDay, startOfToday } from "date-fns";
import DatePicker from "~/components/common/forms/DatePicker";;
import { GetProviderSelectItemsQuery } from "~/graphql/__generated__/graphql";
import { useQuery } from "@apollo/client";
import { useOrgConfig } from "~/providers/OrgConfigProvider";
import { KPIFilterKey, KPIFiltersType, KPIFilterValue } from "../KPIDashboardScreen";
import { GET_PROVIDER_SELECT_ITEMS } from "~/queries/forms";

interface KPIFiltersProps {
  title: string;
  filters: KPIFiltersType;
  onFilterChange: <K extends KPIFilterKey>(
    filter: K,
    value: KPIFilterValue<K>
  ) => void;
}

const KPIFilters: React.FC<KPIFiltersProps> = ({
  title,
  filters,
  onFilterChange,
}) => {

  const dateRange = {
    from: filters.dateRange.from ? startOfDay(filters.dateRange.from) : undefined,
    to: filters.dateRange.to ? endOfDay(filters.dateRange.to) : undefined,
  };

  const { data: providerData } = useQuery<GetProviderSelectItemsQuery>(
    GET_PROVIDER_SELECT_ITEMS,
    {
      onError(error) {
        console.error(error);
      },
    }
  );

  const { serviceLines } = useOrgConfig();

  const handleDateChange = (key: "from" | "to", value: Date) => {
    const newRange = { ...dateRange, [key]: value };

    if (
      key === "from" &&
      newRange.to &&
      isAfter(value as Date, newRange.to)
    ) {
      newRange.to = value;
    }

    if (
      key === "to" &&
      newRange.from &&
      isBefore(value as Date, newRange.from)
    ) {
      newRange.from = value;
    }

    if (newRange.from && newRange.to) {
      onFilterChange("dateRange", {
        from: startOfDay(newRange.from),
        to: endOfDay(newRange.to),
      });
    }
  };

  return (
    <Container maxWidth={false} sx={{ mt: 4, mb: 4 }}>
      <Grid container spacing={2}>
        <Grid flexDirection='row' size={{xs: 12, md: 9}}>
          <Stack direction='row' gap={4}>
            <Grid size={6}>
              <Typography variant="h6" gutterBottom>
                {title}
              </Typography>
            </Grid>

            <FormControl fullWidth size="small">
              <Select
                value={filters.serviceLineId ?? "all"}
                onChange={(e) => {
                  const value = `${e.target.value}`;
                  if (value === "all") {
                    onFilterChange("serviceLineId", undefined);
                    return;
                  }
                  const serviceLineId = parseInt(value);
                  onFilterChange("serviceLineId", serviceLineId);
                }}
                sx={{
                  backgroundColor: "white",
                }}
              >
                <MenuItem value={"all"}>
                  All service lines
                </MenuItem>
                {Object.values(serviceLines).filter(sl => sl !== undefined).map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    {option.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <Select
                size="small"
                value={filters.providerId ?? "all"}
                onChange={(e) => {
                  const value = `${e.target.value}`;
                  if (value === "all") {
                    onFilterChange("providerId", undefined);
                    return;
                  }
                  onFilterChange("providerId", parseInt(value));
                }}
                sx={{
                  backgroundColor: "white",
                }}
              >
                <MenuItem value={"all"}>
                  All providers
                </MenuItem>
                {providerData?.provider?.map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    {option.firstName} {option.lastName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Grid>
        <Grid size={{xs: 12, md: 3}}>
          <Grid size={12}>
            <Box display="flex" gap={2}>
              <DatePicker
                label="From"
                value={dateRange.from}
                onChange={(newValue) => newValue && handleDateChange("from", newValue)}
                maxDate={startOfToday()} // Prevent future dates
                slots={{ textField: TextField }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: "small",
                    sx: { backgroundColor: "white" },
                  },
                }}
              />
              <DatePicker
                label="To"
                value={dateRange.to}
                onChange={(newValue) => newValue && handleDateChange("to", newValue)}
                minDate={dateRange.from}
                maxDate={startOfToday()}
                slots={{ textField: TextField }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: "small",
                    sx: { backgroundColor: "white" },
                  },
                }}
              />
            </Box>
          </Grid>
        </Grid>
      </Grid>
    </Container>
  );
};

export default KPIFilters;
